import { CustomerSubmissionModal } from "@/components/CustomerSubmissionModal";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, X, Trash2, AlertTriangle } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { StatusBadge } from "@/components/StatusBadge";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatMoney } from "@/lib/format";
import { CommunicationsTimeline } from "./communications/Timeline";
import { CustomerPreferencesPanel } from "./communications/PreferencesPanel";

export function StaffCustomerDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [appts, setAppts] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const reload = () => {
        setError(null);
        Promise.all([staffApi.getCustomer(id), staffApi.customerAppointments(id)])
            .then(([c, a]) => { setCustomer(c); setAppts(a.appointments || []); setForm({ firstName: c.firstName || "", lastName: c.lastName || "", email: c.email || "", phone: c.phone || "", countryPhoneIso: c.countryPhoneIso || "gb" }); })
            .catch(setError);
    };
    useEffect(reload, [id]);

    const save = async () => {
        if (busy) return; setBusy(true);
        try { await staffApi.updateCustomer(id, form); toast.success("Customer updated"); setEditing(false); reload(); }
        catch (e) { toast.error(e.message || "Could not update customer"); }
        finally { setBusy(false); }
    };

    const doDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            await staffApi.deleteCustomer(id);
            toast.success("Customer permanently deleted");
            nav("/staff/customers", { replace: true });
        } catch (e) {
            // Backend returns 409 with { code, message, appointment_count } when
            // the customer has history and can't be deleted.
            const detail = e?.response?.data?.detail || e?.data?.detail || e?.detail;
            const msg = typeof detail === "object" && detail?.message
                ? detail.message
                : (e?.message || "Deletion failed");
            toast.error(msg);
            setConfirmDelete(false);
        } finally { setDeleting(false); }
    };

    const stats = React.useMemo(() => {
        if (!appts) return null;
        const past = appts.filter((a) => a.bookingStart && a.bookingStart < new Date().toISOString().replace("T", " "));
        const upcoming = appts.filter((a) => a.bookingStart && a.bookingStart >= new Date().toISOString().replace("T", " "));
        const cancelled = appts.filter((a) => ["canceled","cancelled","rejected","no-show"].includes((a.status || "").toLowerCase()));
        // Total Spend = sum of PAID payment amounts on non-cancelled appointments.
        // Refunded / pending / unpaid balances and cancelled appointments are
        // excluded per the Feb-2026 customer-metric brief.
        const isCancelled = (a) => ["canceled","cancelled","rejected","no-show"].includes((a.status || "").toLowerCase());
        const isPaid = (p) => ["paid","captured","succeeded","completed"].includes((p?.status || "").toLowerCase());
        const totalSpend = appts.reduce((sum, a) => {
            if (isCancelled(a)) return sum;
            const payments = Array.isArray(a.payments) ? a.payments : (a.payment ? [a.payment] : []);
            const paidTotal = payments
                .filter(isPaid)
                .reduce((s, p) => s + (Number(p?.amount) || 0), 0);
            return sum + paidTotal;
        }, 0);
        return { total: appts.length, past: past.length, upcoming: upcoming.length, cancelled: cancelled.length, totalSpend };
    }, [appts]);

    return (
        <div className="space-y-6" data-testid="staff-customer-detail">
            <button onClick={() => nav(-1)} className="text-xs text-white/50 hover:text-gold flex items-center gap-1"><ArrowLeft className="h-3 w-3"/> Back</button>

            {error && <ErrorState error={error} onRetry={reload} />}
            {!customer && !error && <Skeleton className="h-64" />}

            {customer && (
                <>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Customer #{customer.id}</div>
                            <h1 className="mt-2 font-serif font-light text-4xl text-white" data-testid="cust-name">
                                {customer.firstName} {customer.lastName}
                            </h1>
                            <div className="mt-2 text-sm text-white/60 space-y-0.5">
                                <div>{customer.email}</div>
                                <div>{customer.phone}</div>
                            </div>
                        </div>
                        {!editing && (
                            <button onClick={() => setEditing(true)} data-testid="edit-customer" className="rounded-full border hairline text-gold px-4 h-10 text-sm hover:bg-gold/10">
                                Edit customer
                            </button>
                        )}
                    </div>

                    {editing && (
                        <div className="rounded-2xl glass p-5 space-y-3" data-testid="edit-customer-form">
                            <div className="grid grid-cols-2 gap-3">
                                <TextField label="First name" value={form.firstName} onChange={(v) => setForm({...form, firstName: v})} testId="edit-first" />
                                <TextField label="Last name" value={form.lastName} onChange={(v) => setForm({...form, lastName: v})} testId="edit-last" />
                            </div>
                            <TextField label="Email" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} testId="edit-email" />
                            <TextField label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({...form, phone: v})} testId="edit-phone" />
                            <div className="flex gap-2 pt-2">
                                <button onClick={save} disabled={busy} data-testid="save-customer" className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold disabled:opacity-40 flex items-center gap-1"><Save className="h-3.5 w-3.5" /> Save</button>
                                <button onClick={() => setEditing(false)} className="h-10 rounded-full border hairline text-white/60 px-5 text-sm flex items-center gap-1"><X className="h-3.5 w-3.5" /> Cancel</button>
                            </div>

                            {/* Destructive zone — permanent deletion lives here, not on the list */}
                            <div className="mt-6 pt-5 border-t border-red-500/20" data-testid="danger-zone">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-red-300 font-semibold">Danger zone</div>
                                <p className="mt-2 text-xs text-white/60">
                                    Permanently removing a customer erases their profile. If they have any appointment history, deletion is blocked to preserve payment and medical records.
                                </p>
                                <button
                                    onClick={() => setConfirmDelete(true)}
                                    data-testid="delete-customer-btn"
                                    className="mt-3 inline-flex items-center gap-2 h-10 rounded-full border border-red-400/50 text-red-300 hover:bg-red-500/10 px-5 text-sm font-medium"
                                >
                                    <Trash2 className="h-3.5 w-3.5" /> Delete customer permanently
                                </button>
                            </div>
                        </div>
                    )}

                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cust-stats">
                            <Stat label="Appointments" value={stats.total} testId="stat-total" />
                            <Stat label="Upcoming" value={stats.upcoming} testId="stat-upcoming" />
                            <Stat label="Cancelled / no-show" value={stats.cancelled} testId="stat-cancelled" />
                            <Stat label="Total spend" value={formatMoney(stats.totalSpend)} testId="stat-spend" />
                        </div>
                    )}

                    <CustomerFormsPanel customerId={id} />

                    <CustomerPreferencesPanel customerId={id} />

                    <CustomerTimelinePanel customerId={id} />

                    <CommunicationsTimeline scope="customer" id={id} title="Communications history" />

                    <div>
                        <h2 className="font-serif text-2xl text-white mb-3">Appointment history</h2>
                        {!appts ? <Skeleton className="h-32" /> : appts.length === 0 ? (
                            <div className="rounded-2xl glass p-6 text-center text-white/60">No appointments for this customer.</div>
                        ) : (
                            <ul className="space-y-2" data-testid="cust-appt-list">
                                {appts.map((a) => (
                                    <li key={a.id}>
                                        <Link to={`/staff/appointments/${a.id}`} className="block rounded-2xl border hairline p-4 hover:border-gold/60 transition-all">
                                            <div className="flex items-center justify-between gap-4">
                                                <div>
                                                    <div className="flex items-center gap-3 text-xs">
                                                        <span className="text-gold font-ui">{a.bookingStart}</span>
                                                        <StatusBadge status={a.status} />
                                                    </div>
                                                    <div className="text-white text-sm mt-1">{a.serviceName || `Service #${a.serviceId}`}</div>
                                                </div>
                                                <div className="text-right text-sm text-white/70">{formatPrice(a.price)}</div>
                                            </div>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}

            {confirmDelete && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => !deleting && setConfirmDelete(false)}
                    data-testid="delete-customer-confirm"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md rounded-2xl glass glass-strong border hairline p-6 space-y-4"
                    >
                        <div className="flex items-center gap-2 text-red-300">
                            <AlertTriangle className="h-5 w-5" />
                            <h3 className="font-serif text-white text-xl">Delete customer</h3>
                        </div>
                        <p className="text-sm text-white/75">
                            Are you sure you want to permanently delete{" "}
                            <span className="text-white font-medium">
                                {customer?.firstName} {customer?.lastName}
                            </span>
                            ? This action cannot be undone.
                        </p>
                        <p className="text-xs text-white/50">
                            If they have any appointments, forms, payments or history, deletion will be blocked to protect those records.
                        </p>
                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={() => setConfirmDelete(false)}
                                disabled={deleting}
                                data-testid="delete-cancel"
                                className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={doDelete}
                                disabled={deleting}
                                data-testid="delete-confirm"
                                className="flex-1 h-11 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
                            >
                                {deleting ? "Deleting…" : <><Trash2 className="h-4 w-4" /> Delete permanently</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TextField({ label, value, onChange, type = "text", testId }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">{label}</div>
            <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
        </label>
    );
}
function Stat({ label, value, testId }) {
    // All four customer-detail cards share one navy design (Feb-2026 UX brief):
    // navy background, white uppercase label, gold serif value.
    return (
        <div
            className="surface-highlight rounded-2xl p-4 min-w-0 min-h-[104px] flex flex-col justify-between overflow-hidden"
            data-testid={testId}
        >
            <div className="text-[10px] uppercase tracking-[0.18em] leading-tight text-white/85 font-semibold break-words">
                {label}
            </div>
            <div
                className="mt-2 font-serif text-2xl md:text-3xl leading-none text-gold font-medium truncate"
                title={typeof value === "string" ? value : undefined}
            >
                {value}
            </div>
        </div>
    );
}


/* Forms & Documents — completed submissions across every appointment */
function CustomerFormsPanel({ customerId }) {
    const [subs, setSubs] = React.useState(null);
    const [viewSubId, setViewSubId] = React.useState(null);
    React.useEffect(() => {
        staffApi.listCustomerSubmissions(customerId).then((r) => setSubs(r.submissions || [])).catch(() => setSubs([]));
    }, [customerId]);
    return (
        <div className="rounded-2xl glass p-5 space-y-3" data-testid="cust-forms-panel">
            <h2 className="font-serif text-2xl text-white">Forms & documents</h2>
            {!subs ? <Skeleton className="h-16" /> : subs.length === 0 ? (
                <p className="text-white/50 text-sm">No completed forms yet.</p>
            ) : (
                <ul className="divide-y divide-white/5">
                    {subs.map((s) => (
                        <li key={s.id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`cust-sub-${s.id}`}>
                            <div className="min-w-0">
                                <div className="text-white truncate">{s.title_snapshot}</div>
                                <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">
                                    Submitted {new Date(s.submitted_at).toLocaleDateString("en-GB")} · Appt #{s.appointment_id}
                                </div>
                            </div>
                            <button onClick={() => setViewSubId(s.id)} className="text-gold text-[11px] hover:underline">View form →</button>
                        </li>
                    ))}
                </ul>
            )}
            {viewSubId && (
                <CustomerSubmissionModal
                    id={viewSubId}
                    onClose={() => setViewSubId(null)}
                />
            )}
        </div>
    );
}


function CustomerTimelinePanel({ customerId }) {
    const [events, setEvents] = React.useState(null);
    const [error, setError] = React.useState(null);
    React.useEffect(() => {
        staffApi.customerTimeline(customerId)
            .then((r) => setEvents(r.events || []))
            .catch(setError);
    }, [customerId]);
    return (
        <section className="rounded-2xl glass p-4 md:p-5" data-testid="customer-timeline">
            <h2 className="font-serif text-2xl text-white mb-3">Customer timeline</h2>
            {error && <div className="text-xs text-amber-200">Could not load timeline.</div>}
            {!events && !error && <div className="text-xs text-white/50">Loading…</div>}
            {events && events.length === 0 && <div className="text-xs text-white/50">No timeline events yet.</div>}
            {events && events.length > 0 && (
                <ol className="relative border-l border-white/10 pl-4 space-y-3">
                    {events.map((e, i) => (
                        <li key={e.id || i} className="pl-1" data-testid={`timeline-event-${e.event_type}`}>
                            <div className="absolute -left-[5px] mt-1.5 h-2 w-2 rounded-full bg-gold" />
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">{formatTime(e.occurred_at)}</div>
                            <div className="text-sm text-white">{e.title}</div>
                            {e.subtitle && <div className="text-xs text-white/50">{e.subtitle}</div>}
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
}

function formatTime(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
}
